import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import useFundContext from '@/contexts/Fund/useFundContext';
import { useDeleteAuthApi, useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { CreatedUserApiKeyDtoIn, UserApiKeyDtoIn } from '@/models/in/UserApiKeyDtoIn';
import { formatDateLocale } from '@/helpers/dateHelper';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import CopyButton from '@/components/ui/CopyButton/CopyButton';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import styles from './FundSettings.module.css';
import own from './FundApiKeysPage.module.css';

const VALIDITY_OPTIONS = [30, 90, 180, 365] as const;

/** The server's own refusal when it sent one (plain-text bodies), otherwise the generic text. */
const errorText = (err: unknown, fallback: string): string =>
  axios.isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data.trim() ? err.response.data : fallback;

const isActive = (key: UserApiKeyDtoIn) => !key.revokedAt && new Date(key.expiresAt) > new Date();

/**
 * «גישת API» (HAT-10063): the coordinator issues a personal API key for this fund, so an outside
 * system can read the fund's transactions, balance and support requests. The key is shown once, at
 * creation - the server keeps only its hash - and can be revoked here at any moment.
 *
 * Behind the 'public-api' flag, which the server enforces too: on the key endpoints and on every
 * request made with a key.
 */
const FundApiKeysPage = () => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [validDays, setValidDays] = useState<number>(365);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedUserApiKeyDtoIn | null>(null);
  const [toRevoke, setToRevoke] = useState<UserApiKeyDtoIn | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const listUrl = currentFund ? `funds/${currentFund.id}/api-keys` : '';
  const { data: keys, isLoading, isError } = useGetAuthApi<UserApiKeyDtoIn[]>(listUrl);
  const refreshList = () => queryClient.invalidateQueries({ queryKey: [listUrl] });

  const createKey = usePostAuthApi<CreatedUserApiKeyDtoIn, { name: string; validDays: number }>(listUrl, {
    onSuccess: (key) => {
      setCreated(key);
      setName('');
      setCreateError(null);
      refreshList();
    },
    onError: (err) => setCreateError(errorText(err, t('fundApiKeys.createError'))),
  });

  const revokeKey = useDeleteAuthApi<void>(toRevoke ? `${listUrl}/${toRevoke.id}` : '', {
    onSuccess: () => {
      if (created?.id === toRevoke?.id) setCreated(null);
      setToRevoke(null);
      setRevokeError(null);
      refreshList();
    },
    onError: (err) => setRevokeError(errorText(err, t('fundApiKeys.revokeError'))),
  });

  if (!currentFund) return <h1>{t('noFundSelected')}.</h1>;

  const apiBase = `${window.location.origin}/api`;
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setCreateError(t('fundApiKeys.nameRequired'));
      return;
    }
    createKey.mutate({ name: name.trim(), validDays });
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>{t('fundApiKeys.title')}</h1>

      <div className={styles.panel}>
        <p className={own.intro}>{t('fundApiKeys.intro')}</p>
        <form className={own.createForm} onSubmit={submit}>
          <label className={`${own.field} ${own.nameField}`}>
            {t('fundApiKeys.nameLabel')}
            <Input
              value={name}
              maxLength={100}
              placeholder={t('fundApiKeys.namePlaceholder')}
              onChange={(e) => setName(e.target.value)}
              error={!!createError && !name.trim()}
            />
          </label>
          <label className={own.field}>
            {t('fundApiKeys.validityLabel')}
            <select className={own.select} value={validDays} onChange={(e) => setValidDays(Number(e.target.value))}>
              {VALIDITY_OPTIONS.map((days) => (
                <option key={days} value={days}>
                  {t(`fundApiKeys.validity.${days}`)}
                </option>
              ))}
            </select>
          </label>
          <Button type="submit" variant="primary" disabled={createKey.isPending}>
            {createKey.isPending ? t('loading') : t('fundApiKeys.create')}
          </Button>
        </form>
        {createError && <p className={own.error}>{createError}</p>}
      </div>

      {created && (
        <div className={own.newKey} role="status">
          <p className={own.newKeyTitle}>{t('fundApiKeys.newKeyTitle', { name: created.name })}</p>
          <p className={own.intro}>{t('fundApiKeys.newKeyWarning')}</p>
          <div className={own.keyRow}>
            <code className={own.code}>{created.key}</code>
            <CopyButton value={created.key} />
          </div>
          <Button variant="outline" onClick={() => setCreated(null)}>
            {t('fundApiKeys.newKeyDone')}
          </Button>
        </div>
      )}

      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('fundApiKeys.listTitle')}</h2>
        {isError && <p className={styles.noticeError}>{t('fundApiKeys.loadError')}</p>}
        {!isLoading && !isError && (keys?.length ?? 0) === 0 && <p className={styles.empty}>{t('fundApiKeys.empty')}</p>}
        {(keys?.length ?? 0) > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t('fundApiKeys.columns.name')}</th>
                  <th>{t('fundApiKeys.columns.key')}</th>
                  <th>{t('fundApiKeys.columns.created')}</th>
                  <th>{t('fundApiKeys.columns.expires')}</th>
                  <th>{t('fundApiKeys.columns.lastUsed')}</th>
                  <th>{t('fundApiKeys.columns.status')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys!.map((key) => (
                  <tr key={key.id}>
                    <td>{key.name}</td>
                    <td>
                      <code className={own.code}>{key.keyPrefix}…</code>
                    </td>
                    <td>{formatDateLocale(key.createdAt)}</td>
                    <td>{formatDateLocale(key.expiresAt)}</td>
                    <td>{key.lastUsedAt ? formatDateLocale(key.lastUsedAt) : t('fundApiKeys.neverUsed')}</td>
                    <td className={isActive(key) ? own.statusActive : own.statusInactive}>
                      {key.revokedAt ? t('fundApiKeys.status.revoked') : isActive(key) ? t('fundApiKeys.status.active') : t('fundApiKeys.status.expired')}
                    </td>
                    <td>
                      {isActive(key) && (
                        <button type="button" className={styles.linkBtn} onClick={() => setToRevoke(key)}>
                          {t('fundApiKeys.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.panel}>
        <h2 className={styles.panelTitle}>{t('fundApiKeys.usageTitle')}</h2>
        <p className={own.intro}>{t('fundApiKeys.usageIntro')}</p>
        <code className={own.codeBlock}>{`Authorization: Bearer hzk_...`}</code>
        <ul className={own.endpointList}>
          <li>
            {t('fundApiKeys.endpoints.transactions')}{' '}
            <code className={own.code}>{`GET ${apiBase}/funds/financial-data/${currentFund.id}/transactions?start=2026-01-01&end=2026-01-31`}</code>
          </li>
          <li>
            {t('fundApiKeys.endpoints.balance')} <code className={own.code}>{`GET ${apiBase}/funds/financial-data/${currentFund.id}/balance`}</code>
          </li>
          <li>
            {t('fundApiKeys.endpoints.supportRequests')}{' '}
            <code className={own.code}>{`GET ${apiBase}/funds/${currentFund.id}/support-requests?dateFrom=2026-01-01&dateTo=2026-01-31`}</code>
          </li>
        </ul>
        <p className={own.intro}>{t('fundApiKeys.usageLimits')}</p>
      </div>

      <ConfirmationDialog
        open={!!toRevoke}
        onOpenChange={(open) => {
          if (!open) {
            setToRevoke(null);
            setRevokeError(null);
          }
        }}
        title={t('fundApiKeys.revokeTitle')}
        message={toRevoke ? t('fundApiKeys.revokeMessage', { name: toRevoke.name }) : undefined}
        confirmText="fundApiKeys.revoke"
        variant="danger"
        isLoading={revokeKey.isPending}
        error={revokeError}
        onConfirm={() => revokeKey.mutate()}
      />
    </div>
  );
};

export default FundApiKeysPage;
